import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, ChevronDown, Calendar } from 'lucide-react';
import axios from 'axios';

const FAQ_DATA = [
  {
    keywords: ['price', 'cost', 'how much', 'pricing', 'rate', 'charge', 'expensive', 'cheap', 'afford', 'estimate', 'quote'],
    question: 'How much does window tinting cost?',
    answer: 'Pricing depends on vehicle type, number of windows, and film quality. Automotive tinting typically starts around $150-$500. Residential projects vary by square footage. We offer free estimates — just fill out our contact form or call (713) 446-8928!'
  },
  {
    keywords: ['how long', 'time', 'duration', 'take', 'hours', 'wait'],
    question: 'How long does installation take?',
    answer: 'Most automotive tinting takes 1.5 to 3 hours depending on the number of windows. Residential projects may take half a day to a full day. We recommend leaving your vehicle with us for best results.'
  },
  {
    keywords: ['warranty', 'guarantee', 'lifetime', 'coverage'],
    question: 'Do you offer a warranty?',
    answer: 'Yes! We offer a lifetime warranty on our premium ceramic and carbon film installations. This covers bubbling, peeling, cracking, and discoloration. Dyed films carry a 5-year warranty.'
  },
  {
    keywords: ['legal', 'law', 'percent', 'percentage', 'vlt', 'dark', 'darkest', 'limit', 'texas law'],
    question: 'What tint levels are legal in Texas?',
    answer: 'Texas law allows 25% VLT (Visible Light Transmission) on front side windows and any darkness on rear side and back windows. Windshield tinting is allowed above the AS-1 line (top 5 inches). We\'ll help you choose the right level that\'s both legal and effective.'
  },
  {
    keywords: ['ceramic', 'carbon', 'dyed', 'type', 'film', 'kind', 'best', 'difference', 'recommend'],
    question: 'What type of tint film do you recommend?',
    answer: 'We offer three tiers: Ceramic Pro (best heat rejection & clarity), Carbon Series (excellent performance & value), and Dyed Film (budget-friendly option). Ceramic is our most popular choice for Texas heat. We\'ll help you pick the right one for your needs and budget.'
  },
  {
    keywords: ['location', 'address', 'where', 'find', 'directions', 'located'],
    question: 'Where are you located?',
    answer: 'We\'re at 6951 McHard Road, Suite G8, Missouri City, TX 77053. Easy to find with plenty of parking. We serve Houston, Sugar Land, Stafford, Pearland, Richmond, Rosenberg, and all of Fort Bend County.'
  },
  {
    keywords: ['hour', 'open', 'close', 'business hour', 'when', 'sunday', 'saturday', 'weekend'],
    question: 'What are your business hours?',
    answer: 'We\'re open Monday through Friday 9:00 AM to 6:00 PM, and Saturday 9:00 AM to 5:00 PM. We\'re closed on Sundays.'
  },
  {
    keywords: ['residential', 'home', 'house', 'window', 'energy', 'saving'],
    question: 'Do you do residential window tinting?',
    answer: 'Absolutely! We offer residential window tinting that can reduce energy costs by up to 30%, block 99% of UV rays, protect furniture from fading, and improve privacy. We use professional-grade solar control, privacy, and security films.'
  },
  {
    keywords: ['commercial', 'business', 'office', 'store', 'building'],
    question: 'Do you offer commercial tinting?',
    answer: 'Yes! We provide commercial window tinting for offices, storefronts, and buildings. Benefits include energy savings, glare reduction, enhanced privacy, and a professional appearance. Contact us for a free commercial estimate.'
  },
  {
    keywords: ['rain', 'wash', 'care', 'clean', 'maintain', 'after', 'dry'],
    question: 'How do I care for my new tint?',
    answer: 'After installation, wait 3-5 days before rolling down windows (allows the film to cure). Small water bubbles are normal and will disappear within 2-4 weeks. Clean with ammonia-free products only. Avoid abrasive materials.'
  },
  {
    keywords: ['remove', 'old', 'existing', 'strip', 'replace', 'peel'],
    question: 'Can you remove old tint?',
    answer: 'Yes, we offer professional tint removal. If your existing tint is bubbling, purple, or peeling, we can safely remove it and apply fresh film. Removal is typically included when you get a new installation.'
  },
  {
    keywords: ['payment', 'pay', 'cash', 'card', 'credit', 'debit', 'method'],
    question: 'What payment methods do you accept?',
    answer: 'We accept cash, all major credit/debit cards, and digital payments. Payment is due upon completion of the service.'
  },
];

// Keywords that trigger availability check
const AVAILABILITY_KEYWORDS = ['available', 'availability', 'open slot', 'opening', 'free slot', 'appointment', 'book', 'schedule', 'reservation', 'slot', 'next available', 'tomorrow', 'today', 'this week', 'next week', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Days of week for parsing
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const QUICK_QUESTIONS = [
  'How much does tinting cost?',
  'What tint is legal in Texas?',
  'What times are available this week?',
  'Where are you located?',
  'What are your hours?',
];

function parseRequestedDate(input) {
  const lower = input.toLowerCase();
  const today = new Date();

  if (lower.includes('today')) {
    return [today];
  }

  if (lower.includes('tomorrow')) {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return [d];
  }

  // Check for day names
  for (let i = 0; i < DAY_NAMES.length; i++) {
    if (lower.includes(DAY_NAMES[i])) {
      const d = new Date(today);
      const diff = (i - today.getDay() + 7) % 7 || 7; // next occurrence
      d.setDate(d.getDate() + diff);
      return [d];
    }
  }

  // "this week" or "next week" or general availability
  if (lower.includes('this week') || lower.includes('next week') || lower.includes('available') || lower.includes('availability') || lower.includes('open') || lower.includes('slot') || lower.includes('next available')) {
    const isNextWeek = lower.includes('next week');
    const dates = [];
    const startOffset = isNextWeek ? (7 - today.getDay() + 1) : 1; // next Mon or tomorrow

    for (let i = 0; i < 6; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + startOffset + i);
      // Skip Sunday (0)
      if (d.getDay() !== 0) {
        dates.push(d);
        if (dates.length >= 3) break; // show 3 days max
      }
    }
    return dates;
  }

  return null;
}

function formatDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateDisplay(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function isAvailabilityQuery(input) {
  const lower = input.toLowerCase();
  return AVAILABILITY_KEYWORDS.some(kw => lower.includes(kw));
}

function findFaqAnswer(input) {
  const lower = input.toLowerCase();
  let bestMatch = null;
  let bestScore = 0;

  for (const faq of FAQ_DATA) {
    let score = 0;
    for (const kw of faq.keywords) {
      if (lower.includes(kw)) score += kw.split(' ').length;
    }
    if (score > bestScore) {
      bestScore = score;
      bestMatch = faq;
    }
  }

  if (bestScore > 0) return bestMatch.answer;

  if (/^(hi|hello|hey|howdy|sup|what'?s up|good (morning|afternoon|evening))/.test(lower)) {
    return "Hey there! 👋 Welcome to Texas Tint Studio. I can help with pricing, services, hours, availability, and more. What would you like to know?";
  }

  if (/^(thanks|thank you|thx|ty|appreciate)/.test(lower)) {
    return "You're welcome! If you have any other questions, feel free to ask. Or call us at (713) 446-8928!";
  }

  return null;
}

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hi! 👋 I'm the Texas Tint Studio assistant. Ask me about pricing, services, hours, or check available appointment times!" }
  ]);
  const [input, setInput] = useState('');
  const [showQuickQ, setShowQuickQ] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, { from: 'bot', text }]);
  };

  const addBotAvailability = (data) => {
    setMessages(prev => [...prev, { from: 'bot', text: data.text, slots: data.slots }]);
  };

  const fetchAvailability = async (dates) => {
    setIsTyping(true);
    try {
      const results = [];
      for (const date of dates) {
        const dateStr = formatDateStr(date);
        const dayOfWeek = date.getDay();

        // Sunday check
        if (dayOfWeek === 0) {
          results.push({ date: formatDateDisplay(date), dateStr, slots: [], closed: true });
          continue;
        }

        try {
          const res = await axios.get(`/api/reservations/available-slots?date_str=${dateStr}`);
          results.push({
            date: formatDateDisplay(date),
            dateStr,
            slots: res.data.slots || [],
            closed: false
          });
        } catch {
          results.push({ date: formatDateDisplay(date), dateStr, slots: [], closed: false, error: true });
        }
      }

      // Build response
      let text = "Here's what's available:\n\n";
      let hasSlots = false;

      for (const r of results) {
        if (r.closed) {
          text += `📅 **${r.date}** — Closed (Sunday)\n`;
        } else if (r.error) {
          text += `📅 **${r.date}** — Couldn't check availability\n`;
        } else if (r.slots.length === 0) {
          text += `📅 **${r.date}** — Fully booked\n`;
        } else {
          hasSlots = true;
          text += `📅 **${r.date}** — ${r.slots.length} slot${r.slots.length > 1 ? 's' : ''} open\n`;
        }
      }

      if (hasSlots) {
        text += "\nWould you like to book? Head to our Book Now page or call (713) 446-8928!";
      } else {
        text += "\nLooks like those days are busy! Try asking about different dates, or call us at (713) 446-8928 and we'll find a time that works.";
      }

      addBotAvailability({ text, slots: results });
    } catch {
      addBotMessage("Sorry, I couldn't check availability right now. Please call us at (713) 446-8928 or use our Book Now page.");
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = (text) => {
    const msg = text || input.trim();
    if (!msg) return;

    setMessages(prev => [...prev, { from: 'user', text: msg }]);
    setInput('');
    setShowQuickQ(false);

    // Check if it's an availability query
    if (isAvailabilityQuery(msg)) {
      const dates = parseRequestedDate(msg);
      if (dates && dates.length > 0) {
        fetchAvailability(dates);
        return;
      }
      // Generic availability — show next 3 business days
      const upcoming = [];
      const today = new Date();
      let offset = 1;
      while (upcoming.length < 3) {
        const d = new Date(today);
        d.setDate(d.getDate() + offset);
        if (d.getDay() !== 0) upcoming.push(d);
        offset++;
      }
      fetchAvailability(upcoming);
      return;
    }

    // Regular FAQ
    setTimeout(() => {
      const answer = findFaqAnswer(msg);
      if (answer) {
        addBotMessage(answer);
      } else {
        addBotMessage("I'm not sure about that one, but our team can help! Call us at (713) 446-8928 or fill out our contact form and we'll get back to you within 24 hours.");
      }
    }, 400);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderMessage = (msg, i) => {
    if (msg.from === 'user') {
      return (
        <div key={i} className="flex justify-end">
          <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed bg-red-600 text-white">
            {msg.text}
          </div>
        </div>
      );
    }

    return (
      <div key={i} className="flex justify-start">
        <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-bl-md text-sm leading-relaxed bg-white text-gray-800 border border-gray-200 shadow-sm">
          {/* Render text with basic formatting */}
          {msg.text.split('\n').map((line, li) => {
            // Bold **text**
            const parts = line.split(/\*\*(.*?)\*\*/g);
            return (
              <p key={li} className={li > 0 ? 'mt-1' : ''}>
                {parts.map((part, pi) =>
                  pi % 2 === 1 ? <strong key={pi}>{part}</strong> : part
                )}
              </p>
            );
          })}

          {/* Show slot pills if available */}
          {msg.slots && msg.slots.some(r => r.slots.length > 0) && (
            <div className="mt-3 space-y-2">
              {msg.slots.filter(r => r.slots.length > 0).map((r, ri) => (
                <div key={ri}>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{r.date}:</p>
                  <div className="flex flex-wrap gap-1">
                    {r.slots.slice(0, 6).map((slot, si) => (
                      <span key={si} className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                        {slot.display}
                      </span>
                    ))}
                    {r.slots.length > 6 && (
                      <span className="text-xs text-gray-500 px-1">+{r.slots.length - 6} more</span>
                    )}
                  </div>
                </div>
              ))}
              <a href="/book" className="inline-flex items-center text-xs text-red-600 font-medium hover:underline mt-1">
                <Calendar className="h-3 w-3 mr-1" /> Book an appointment →
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Chat bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-red-600 hover:bg-red-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center hover:scale-105"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white"></span>
        </button>
      )}

      {/* Chat window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" style={{ height: '500px' }}>
          {/* Header */}
          <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center space-x-2">
              <MessageCircle className="h-5 w-5" />
              <div>
                <p className="font-semibold text-sm">Texas Tint Studio</p>
                <p className="text-xs text-red-200">FAQ & Availability</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-red-700 rounded-full p-1 transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, i) => renderMessage(msg, i))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex space-x-1.5">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick questions */}
            {showQuickQ && (
              <div className="space-y-1.5 pt-1">
                <p className="text-xs text-gray-500 font-medium">Popular questions:</p>
                {QUICK_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    className="block w-full text-left text-sm px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors text-gray-700"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t bg-white p-3 flex-shrink-0">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about times, pricing..."
                className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-400"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isTyping}
                className="w-9 h-9 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-full flex items-center justify-center transition-colors flex-shrink-0"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            {!showQuickQ && (
              <button onClick={() => setShowQuickQ(true)} className="text-xs text-gray-400 hover:text-red-500 mt-1.5 flex items-center space-x-1">
                <ChevronDown className="h-3 w-3" /> <span>Show suggested questions</span>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
